/**
 * Damage-case chat Firebase boundary.
 *
 * ALPC uses Firebase only as the realtime chat transport for damage-case screens.
 * The master damage-case apps still own durable comments/history:
 * - emil_frey uses ELP / alpapi Firebase and backend APIs.
 * - vehicle_logistics uses ELP_PG / alpapi_pg Firebase and backend APIs.
 *
 * The chat token is the provider damage-code id stored locally in `emil_frey_id`
 * for both providers. Do not add notifications, counters, online status, or
 * app-wide ALPC Firebase startup here; those were intentionally removed.
 */
import firebase from 'firebase/app';
import 'firebase/firestore';

export type DamageCaseFirebaseProvider = 'emil_frey' | 'vehicle_logistics';

export interface DamageCaseFirebaseClient {
    provider: DamageCaseFirebaseProvider;
    firebase: typeof firebase;
    firestore: firebase.firestore.Firestore;
}

// ELP / alpapi Firebase config for Emil Frey damage-case chat.
// Replace this block when you share the real Emil Frey Firebase web config.
const emilFreyFirebaseConfig = {
    apiKey: "AIzaSyBdD_3MTWVuzgZquO-MLUPiIwDIpBKhppc",
    authDomain: "alpstage-277d0.firebaseapp.com",
    databaseURL: "https://alpstage-277d0-default-rtdb.europe-west1.firebasedatabase.app",
    projectId: "alpstage-277d0",
    storageBucket: "alpstage-277d0.firebasestorage.app",
    messagingSenderId: "316265769435",
    appId: "1:316265769435:web:d924b41ddc32d7182f1ffe"
};

// ELP_PG / alpapi_pg Firebase config for Vehicle Logistics damage-case chat.
// Currently copied from Emil Frey; replace this block with the ELP_PG Firebase web config.
const vehicleLogisticsFirebaseConfig = {
    apiKey: "AIzaSyAtFfEfwCTZqxzXMk2wKDr4zYrPOC3vLeQ",
    authDomain: "vlcz-prod.firebaseapp.com",
    databaseURL: "https://vlcz-prod-default-rtdb.firebaseio.com",
    projectId: "vlcz-prod",
    storageBucket: "vlcz-prod.firebasestorage.app",
    messagingSenderId: "453795211510",
    appId: "1:453795211510:web:df5825ad3592bc87ade4d7",
    measurementId: "G-3S4WPM14GB"
};

const damageCaseFirebaseConfigs: Record<DamageCaseFirebaseProvider, typeof emilFreyFirebaseConfig> = {
    emil_frey: emilFreyFirebaseConfig,
    vehicle_logistics: vehicleLogisticsFirebaseConfig,
};

const appNames: Record<DamageCaseFirebaseProvider, string> = {
    emil_frey: 'damage-case-emil-frey',
    vehicle_logistics: 'damage-case-vehicle-logistics',
};

export function resolveDamageCaseFirebaseProvider(sourceType?: string | null): DamageCaseFirebaseProvider {
    return sourceType === 'vehicle_logistics' ? 'vehicle_logistics' : 'emil_frey';
}

export function getDamageCaseFirebaseClient(provider: DamageCaseFirebaseProvider): DamageCaseFirebaseClient {
    const appName = appNames[provider];
    const app = firebase.apps.find(existingApp => existingApp.name === appName)
        ?? firebase.initializeApp(damageCaseFirebaseConfigs[provider], appName);

    return {
        provider,
        firebase,
        firestore: app.firestore(),
    };
}
